import { openDB } from "idb";

const DB_NAME = "webapp_contextstory_ai";
const DB_VERSION = 2;
const PROFILES_STORE = "voice_profiles";
const SETTINGS_STORE = "settings";

const getDB = () =>
  openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(PROFILES_STORE)) {
        db.createObjectStore(PROFILES_STORE, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(SETTINGS_STORE)) {
        db.createObjectStore(SETTINGS_STORE, { keyPath: "key" });
      }
    },
  });

const STARTER_MODES_WRITING = [
  {
    id: "mode_cold_outreach",
    modeType: "writing",
    name: "Cold Outreach",
    formality: 4,
    contractions: "Sometimes",
    greeting: "Hi [name]",
    signoff: "Best",
    length: "short_paragraph",
    emojis: "Never",
    toneArchetype: "text_friendly",
    voiceSample: "",
    voiceSamples: [
      "Hi Sarah, I came across your work at Meridian and was impressed by what your team has done with real-time analytics. We help companies like yours cut pipeline latency by 40% — would love to share how. Free for a quick 15-min call this week?",
    ],
    customInstructions: "- Lead with a specific observation about the recipient or their company\n- If company knowledge is available, weave in relevant product/service value naturally\n- Keep to one short paragraph plus a clear CTA\n- Never use generic openers like \"I hope this email finds you well\"",
    defaultPrompt: "Write a professional cold outreach email. Introduce myself, reference my company's relevant offerings, and explain how I can provide value to them. Keep it concise and include a clear call to action.",
    isStarter: true,
  },
  {
    id: "mode_warm_followup",
    modeType: "writing",
    name: "Warm Follow-up",
    formality: 3,
    contractions: "Always",
    greeting: "Hey [name]",
    signoff: "Thanks",
    length: "one_two_sentences",
    emojis: "Rarely",
    toneArchetype: "text_friendly",
    voiceSample: "",
    voiceSamples: [
      "Hey Mike, great chatting with you at the conference yesterday! Wanted to follow up on what we discussed about the integration timeline. I'll send over that proposal by Friday — let me know if anything else comes to mind before then.",
    ],
    customInstructions: "- Reference the previous interaction or shared context\n- If relevant, tie in how your company can help with what was discussed\n- Keep it brief — one to two sentences max\n- End with a soft next step, not a hard ask",
    defaultPrompt: "Write a warm follow-up email referencing our previous interaction. Keep it friendly, mention how we can help, and suggest next steps.",
    isStarter: true,
  },
  {
    id: "mode_formal",
    modeType: "writing",
    name: "Formal / Executive",
    formality: 5,
    contractions: "Never",
    greeting: "Hello [name]",
    signoff: "Regards",
    length: "few_paragraphs",
    emojis: "Never",
    toneArchetype: "text_professional",
    voiceSample: "",
    voiceSamples: [
      "Hello Dr. Patel, Thank you for taking the time to discuss the partnership framework during our call on Monday. I have reviewed the terms with our legal team and would like to propose a revised timeline that accounts for the regulatory considerations you raised. Please find the updated proposal attached. I look forward to your feedback at your earliest convenience.",
    ],
    customInstructions: "- Use full sentences, no abbreviations or slang\n- Structure with clear paragraphs: context, body, next steps\n- Reference company capabilities where relevant to the discussion\n- Address the recipient by title when appropriate",
    defaultPrompt: "Write a formal, executive-level email. Use professional language, reference our company where relevant, and maintain a polished tone throughout.",
    isStarter: true,
  },
  {
    id: "mode_casual",
    modeType: "writing",
    name: "Casual / Personal",
    formality: 2,
    contractions: "Always",
    greeting: "Hey [name]",
    signoff: "Talk soon",
    length: "one_two_sentences",
    emojis: "Sometimes",
    toneArchetype: "text_playful",
    voiceSample: "",
    voiceSamples: [
      "Hey Jess! Just saw the photos from your trip — looks like it was amazing! Anyway, wanted to see if you're free for coffee next week. It's been way too long. Let me know what works!",
    ],
    customInstructions: "- Write like you're texting a friend, but in email form\n- Keep it short and breezy\n- It's fine to use exclamation marks and informal language",
    defaultPrompt: "Write a casual, personal email. Keep it light, friendly, and conversational.",
    isStarter: true,
  },
  {
    id: "mode_thankyou",
    modeType: "writing",
    name: "Thank-You",
    formality: 3,
    contractions: "Always",
    greeting: "Hi [name]",
    signoff: "Thanks",
    length: "one_two_sentences",
    emojis: "Rarely",
    toneArchetype: "direct_warm",
    voiceSample: "",
    voiceSamples: [
      "Hi Alex, just wanted to say thank you for the intro to your team at Acme. The conversation was really productive and I think there's a lot we can do together. I really appreciate you making that connection.",
    ],
    customInstructions: "- Be specific about what you're thankful for\n- Keep it genuine — don't over-embellish\n- One to two sentences is perfect",
    defaultPrompt: "Write a sincere thank-you email expressing gratitude. Keep it brief and genuine.",
    isStarter: true,
  },
];

const STARTER_MODES_TEXT = [
  {
    id: "text_mode_cold_outreach",
    modeType: "text",
    name: "Cold Intro Text",
    formality: 3,
    contractions: "Always",
    greeting: "Hi [name]",
    signoff: "- [your name]",
    length: "one_two_sentences",
    emojis: "Rarely",
    toneArchetype: "direct_warm",
    voiceSample: "",
    voiceSamples: [
      "Hi Sarah, this is Alex from Acme. We help teams cut response times by 40%. Open to a quick 10-min call this week?",
    ],
    customInstructions: "- Keep it short enough for SMS\n- One clear ask\n- Avoid buzzwords and long setup",
    defaultPrompt: "Write a concise outreach text message with one clear call to action.",
    isStarter: true,
  },
  {
    id: "text_mode_followup",
    modeType: "text",
    name: "Follow-up Text",
    formality: 2,
    contractions: "Always",
    greeting: "Hey [name]",
    signoff: "",
    length: "one_two_sentences",
    emojis: "Sometimes",
    toneArchetype: "direct_warm",
    voiceSample: "",
    voiceSamples: [
      "Hey Mike, following up on our chat yesterday. Want me to send over two time options for next week?",
    ],
    customInstructions: "- Friendly and direct\n- Mention prior context quickly\n- End with an easy yes/no reply",
    defaultPrompt: "Write a short follow-up text that references prior context and asks for a quick reply.",
    isStarter: true,
  },
  {
    id: "text_mode_reminder",
    modeType: "text",
    name: "Reminder Text",
    formality: 3,
    contractions: "Always",
    greeting: "Hi [name]",
    signoff: "",
    length: "one_two_sentences",
    emojis: "Never",
    toneArchetype: "polished_formal",
    voiceSample: "",
    voiceSamples: [
      "Hi Dana, quick reminder about tomorrow at 2:00 PM. Reply here if you need to shift the time.",
    ],
    customInstructions: "- Keep it clear and practical\n- Include time/date if provided\n- Avoid extra fluff",
    defaultPrompt: "Write a clear reminder text message in a professional tone.",
    isStarter: true,
  },
  {
    id: "text_mode_thankyou",
    modeType: "text",
    name: "Thank-you Text",
    formality: 2,
    contractions: "Always",
    greeting: "Hi [name]",
    signoff: "",
    length: "one_two_sentences",
    emojis: "Rarely",
    toneArchetype: "casual_playful",
    voiceSample: "",
    voiceSamples: [
      "Thanks again for the intro today, really appreciate it. I’ll follow up with next steps by Friday.",
    ],
    customInstructions: "- Be warm and specific\n- Keep it brief\n- Mention next step when relevant",
    defaultPrompt: "Write a short thank-you text that feels genuine and specific.",
    isStarter: true,
  },
];

const getStarterModesForType = (modeType = "writing") => (
  modeType === "text" ? STARTER_MODES_TEXT : STARTER_MODES_WRITING
);

export const saveVoiceProfile = async (profile) => {
  const db = await getDB();
  const next = { ...profile };
  next.modeType = next.modeType || "writing";
  next.updatedAt = Date.now();
  if (!next.createdAt) next.createdAt = Date.now();
  await db.put(PROFILES_STORE, next);
  return next;
};

export const getAllVoiceProfiles = async (modeType = "writing") => {
  const db = await getDB();
  const all = await db.getAll(PROFILES_STORE);
  return all.filter((p) => {
    const type = p?.modeType || "writing";
    return type === modeType;
  });
};

export const deleteVoiceProfile = async (id) => {
  const db = await getDB();
  await db.delete(PROFILES_STORE, id);
};

export const getCompanyKnowledge = async () => {
  const db = await getDB();
  const record = await db.get(SETTINGS_STORE, "company_knowledge");
  return record?.value || "";
};

export const saveCompanyKnowledge = async (text) => {
  const db = await getDB();
  await db.put(SETTINGS_STORE, { key: "company_knowledge", value: text, updatedAt: Date.now() });
};

export const ensureStarterModes = async (modeType = "writing") => {
  const db = await getDB();
  const starters = getStarterModesForType(modeType);
  const existing = await db.getAll(PROFILES_STORE);
  const existingMap = new Map(existing.map((p) => [p.id, p]));
  const now = Date.now();

  for (const mode of starters) {
    const current = existingMap.get(mode.id);
    if (!current) {
      await db.put(PROFILES_STORE, { ...mode, createdAt: now, updatedAt: now });
    } else if (current.isStarter) {
      let patched = false;
      const updates = {};
      if (!current.modeType) { updates.modeType = modeType; patched = true; }
      if (!current.defaultPrompt && mode.defaultPrompt) { updates.defaultPrompt = mode.defaultPrompt; patched = true; }
      if ((!current.voiceSamples || current.voiceSamples.length === 0) && mode.voiceSamples?.length) { updates.voiceSamples = mode.voiceSamples; patched = true; }
      if (!current.customInstructions && mode.customInstructions) { updates.customInstructions = mode.customInstructions; patched = true; }
      if (patched) {
        await db.put(PROFILES_STORE, { ...current, ...updates, updatedAt: now });
      }
    }
  }
};
